import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Brain, 
  Loader2,
  TrendingUp,
  Hash,
  FileText,
  BarChart3,
  PieChart,
  Sparkles,
  Target,
  Lightbulb,
  Clock,
  ThumbsUp,
  MessageSquare,
  Share2,
  Eye,
  Plus,
  RefreshCw,
  ChevronRight,
  Award,
  Zap,
  Users
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton, SkeletonCard, SkeletonChart } from "../components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PerformanceRecordDialog from "../components/learning/PerformanceRecordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#DC143C', '#D4AF37', '#22c55e', '#3b82f6', '#a855f7', '#f97316', '#ec4899', '#06b6d4'];

const RECOMMENDATION_ICONS = {
  topic: Target,
  hook: Zap,
  format: FileText,
  timing: Clock
};

const RECOMMENDATION_COLORS = {
  topic: "text-[#DC143C]",
  hook: "text-amber-500",
  format: "text-emerald-500",
  timing: "text-blue-500"
};

export default function Learning() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [content, setContent] = useState([]);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingRecs, setGeneratingRecs] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Competitor comparison state
  const [competitorComparison, setCompetitorComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Performance recording state
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [performanceForm, setPerformanceForm] = useState({
    platform: "linkedin",
    likes: 0,
    comments: 0,
    shares: 0,
    impressions: 0,
    hook_used: "",
    notes: ""
  });
  const [recording, setRecording] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [contentRes, statsRes, recsRes] = await Promise.all([
        axios.get(`${API}/content?region=${region}&brand=${brand}`),
        axios.get(`${API}/performance/stats/${brand}/${region}`),
        axios.get(`${API}/recommendations/${brand}/${region}`)
      ]);
      setContent(contentRes.data);
      setPerformanceStats(statsRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      console.error("Error fetching learning data:", error);
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData, region, brand]);

  const generateRecommendations = async () => {
    setGeneratingRecs(true);
    try {
      await axios.post(`${API}/recommendations/generate/${brand}/${region}`);
      toast.success("Generating AI recommendations...");
      // Poll for results
      setTimeout(async () => {
        const recsRes = await axios.get(`${API}/recommendations/${brand}/${region}`);
        setRecommendations(recsRes.data);
        setGeneratingRecs(false);
        toast.success("Recommendations ready!");
      }, 5000);
    } catch (error) {
      setGeneratingRecs(false);
      toast.error("Failed to generate recommendations");
    }
  };

  const fetchCompetitorComparison = useCallback(async () => {
    setLoadingComparison(true);
    try {
      const res = await axios.get(`${API}/analytics/competitor-comparison/${brand}/${region}`);
      setCompetitorComparison(res.data);
    } catch (error) {
      console.error("Error fetching comparison:", error);
      toast.error("Failed to load competitor comparison");
    } finally {
      setLoadingComparison(false);
    }
  }, [brand, region]);

  // Load comparison data when tab is selected
  useEffect(() => {
    if (activeTab === "comparison" && !competitorComparison) {
      fetchCompetitorComparison();
    }
  }, [activeTab, competitorComparison, fetchCompetitorComparison]);

  const openRecordDialog = (contentItem) => {
    setSelectedContent(contentItem);
    setPerformanceForm({
      platform: "linkedin",
      likes: 0,
      comments: 0,
      shares: 0,
      impressions: 0,
      hook_used: "",
      notes: ""
    });
    setRecordDialogOpen(true);
  };

  const recordPerformance = async () => {
    if (!selectedContent) return;
    setRecording(true);
    try {
      await axios.post(`${API}/performance/record/${brand}/${region}`, {
        content_id: selectedContent.id,
        ...performanceForm
      });
      toast.success("Performance recorded!");
      setRecordDialogOpen(false);
      // Refresh stats
      const statsRes = await axios.get(`${API}/performance/stats/${brand}/${region}`);
      setPerformanceStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to record performance");
    } finally {
      setRecording(false);
    }
  };

  // Analyze patterns from content
  const analyzePatterns = () => {
    const topicCategories = {};
    const hookStyles = {};
    const emotionalTriggers = {};
    
    content.forEach(item => {
      if (item.topic_category) {
        topicCategories[item.topic_category] = (topicCategories[item.topic_category] || 0) + 1;
      }
      if (item.emotional_trigger) {
        emotionalTriggers[item.emotional_trigger] = (emotionalTriggers[item.emotional_trigger] || 0) + 1;
      }
      item.hook_variants?.forEach(hook => {
        let style;
        if (hook.style) {
          style = hook.style;
        } else {
          const keys = Object.keys(hook);
          style = keys[0];
        }
        if (style) {
          hookStyles[style] = (hookStyles[style] || 0) + 1;
        }
      });
    });
    
    return { topicCategories, hookStyles, emotionalTriggers };
  };

  const patterns = analyzePatterns();

  const categoryData = Object.entries(patterns.topicCategories).map(([name, value]) => ({
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    fullName: name,
    count: value
  }));

  const hookData = Object.entries(patterns.hookStyles).map(([name, value]) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  }));

  const triggerData = Object.entries(patterns.emotionalTriggers).map(([name, value]) => ({
    name: name.length > 12 ? name.substring(0, 12) + '...' : name,
    fullName: name,
    count: value
  }));

  // Performance metrics
  const stats = performanceStats || { total_posts: 0, avg_engagement_rate: 0, total_impressions: 0, total_engagement: 0 };

  const metrics = [
    { label: "Content Generated", value: content.length, icon: FileText, color: "text-[#DC143C]", bg: "bg-[#DC143C]/10" },
    { label: "Posts Tracked", value: stats.total_posts, icon: BarChart3, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Avg Engagement", value: `${stats.avg_engagement_rate}%`, icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "AI Insights", value: recommendations.length, icon: Sparkles, color: "text-purple-500", bg: "bg-purple-500/10" },
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6" data-testid="learning-loading">
        <div className="animate-fadeIn">
          <Skeleton className={`h-8 w-48 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          <Skeleton className={`h-4 w-64 ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`animate-fadeIn stagger-${i}`}>
              <SkeletonCard darkMode={darkMode} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="animate-fadeIn stagger-5">
            <SkeletonChart darkMode={darkMode} height="h-[300px]" />
          </div>
          <div className="animate-fadeIn stagger-6">
            <SkeletonChart darkMode={darkMode} height="h-[300px]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="learning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Memory & Learning
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Track performance, learn from data, improve content
          </p>
        </div>
        <Button
          onClick={generateRecommendations}
          disabled={generatingRecs}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="generate-recommendations-btn"
        >
          {generatingRecs ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Insights
            </>
          )}
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div 
            key={metric.label}
            className={`rounded-xl p-5 animate-fadeIn stagger-${index + 1} ${
              darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'
            }`}
            data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {metric.label}
              </span>
              <div className={`p-2 rounded-lg ${metric.bg}`}>
                <metric.icon className={`w-4 h-4 ${metric.color}`} />
              </div>
            </div>
            <p className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-lg w-fit ${darkMode ? 'bg-[#161b22]' : 'bg-gray-100'}`}>
        {[
          { id: "overview", label: "Overview" },
          { id: "recommendations", label: "AI Recommendations" },
          { id: "comparison", label: "Competitor Comparison" },
          { id: "record", label: "Record Performance" }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? darkMode 
                  ? 'bg-[#21262d] text-white' 
                  : 'bg-white text-gray-900 shadow-sm'
                : darkMode
                  ? 'text-gray-400 hover:text-white'
                  : 'text-gray-500 hover:text-gray-900'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* AI Recommendations Summary */}
          {recommendations.length > 0 && (
            <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-gradient-to-r from-purple-900/20 to-[#161b22] border border-purple-500/20' : 'bg-gradient-to-r from-purple-50 to-white border border-purple-100'}`}>
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-purple-500/20' : 'border-purple-100'}`}>
                <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Top AI Recommendation
                </h3>
              </div>
              <div className="p-5">
                <div className="flex items-start gap-4">
                  {(() => {
                    const topRec = recommendations[0];
                    const Icon = RECOMMENDATION_ICONS[topRec.recommendation_type] || Lightbulb;
                    return (
                      <>
                        <div className={`p-3 rounded-xl ${darkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                          <Icon className="w-6 h-6 text-purple-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {topRec.title}
                          </h4>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {topRec.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3">
                            <Badge className="bg-purple-100 text-purple-700 border-0">
                              {Math.round(topRec.confidence_score * 100)}% confidence
                            </Badge>
                            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              Based on {topRec.based_on_data_points} data points
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Topic Categories */}
            <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="chart-categories">
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <BarChart3 className="w-5 h-5 text-[#DC143C]" />
                  Topic Categories
                </h3>
              </div>
              <div className="p-5">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis type="number" stroke={darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.5)'} />
                      <YAxis dataKey="name" type="category" stroke={darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.5)'} width={100} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#161b22' : '#fff', 
                          border: `1px solid ${darkMode ? '#30363d' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => [value, props.payload.fullName || name]}
                      />
                      <Bar dataKey="count" fill="#DC143C" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={`h-[300px] flex items-center justify-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No data available yet
                  </div>
                )}
              </div>
            </div>

            {/* Hook Styles Pie Chart */}
            <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="chart-hooks">
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <PieChart className="w-5 h-5 text-[#D4AF37]" />
                  Hook Styles Distribution
                </h3>
              </div>
              <div className="p-5">
                {hookData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RePieChart>
                      <Pie
                        data={hookData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {hookData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#161b22' : '#fff', 
                          border: `1px solid ${darkMode ? '#30363d' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                      />
                    </RePieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={`h-[300px] flex items-center justify-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No data available yet
                  </div>
                )}
              </div>
            </div>

            {/* Emotional Triggers */}
            <div className={`rounded-xl overflow-hidden lg:col-span-2 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`} data-testid="chart-triggers">
              <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                <h3 className={`font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Brain className="w-5 h-5 text-emerald-500" />
                  Emotional Triggers Used
                </h3>
              </div>
              <div className="p-5">
                {triggerData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={triggerData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="name" stroke={darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.5)'} tick={{ fontSize: 11 }} />
                      <YAxis stroke={darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.5)'} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#161b22' : '#fff', 
                          border: `1px solid ${darkMode ? '#30363d' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => [value, props.payload.fullName || name]}
                      />
                      <Bar dataKey="count" fill="#22c55e" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={`h-[250px] flex items-center justify-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No data available yet
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Performing Content */}
          {stats.top_hooks?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Top Hooks */}
              <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                  <h3 className={`font-semibold text-sm flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Award className="w-4 h-4 text-amber-500" />
                    Top Performing Hooks
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  {stats.top_hooks.map((hook, i) => (
                    <div key={hook.hook} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-amber-500 text-white' : darkMode ? 'bg-[#21262d] text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}>{i + 1}</span>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{hook.hook}</span>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                        {hook.avg_rate.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Categories */}
              <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                  <h3 className={`font-semibold text-sm flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Target className="w-4 h-4 text-[#DC143C]" />
                    Top Categories
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  {stats.top_categories?.map((cat, i) => (
                    <div key={cat.category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-[#DC143C] text-white' : darkMode ? 'bg-[#21262d] text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}>{i + 1}</span>
                        <span className={`text-sm truncate max-w-[120px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{cat.category}</span>
                      </div>
                      <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0 text-xs">
                        {cat.avg_rate.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Triggers */}
              <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                  <h3 className={`font-semibold text-sm flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Brain className="w-4 h-4 text-purple-500" />
                    Top Triggers
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  {stats.top_triggers?.map((trigger, i) => (
                    <div key={trigger.trigger} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                          i === 0 ? 'bg-purple-500 text-white' : darkMode ? 'bg-[#21262d] text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}>{i + 1}</span>
                        <span className={`text-sm truncate max-w-[120px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{trigger.trigger}</span>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">
                        {trigger.avg_rate.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === "recommendations" && (
        <div className="space-y-4">
          {recommendations.length > 0 ? (
            recommendations.map((rec, index) => {
              const Icon = RECOMMENDATION_ICONS[rec.recommendation_type] || Lightbulb;
              const colorClass = RECOMMENDATION_COLORS[rec.recommendation_type] || "text-gray-500";
              return (
                <div 
                  key={rec.id || index}
                  className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
                  data-testid={`recommendation-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                      <Icon className={`w-6 h-6 ${colorClass}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={`mb-2 ${darkMode ? 'bg-[#21262d] text-gray-300' : 'bg-gray-100 text-gray-600'} border-0`}>
                            {rec.recommendation_type.charAt(0).toUpperCase() + rec.recommendation_type.slice(1)}
                          </Badge>
                          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {rec.title}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            rec.confidence_score >= 0.8 
                              ? 'bg-emerald-100 text-emerald-700'
                              : rec.confidence_score >= 0.6
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-600'
                          }`}>
                            {Math.round(rec.confidence_score * 100)}% confident
                          </div>
                        </div>
                      </div>
                      <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {rec.description}
                      </p>
                      <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Based on {rec.based_on_data_points} data points • Generated {new Date(rec.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={`rounded-xl p-12 text-center ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
              <Sparkles className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>No recommendations yet</h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Click "Generate AI Insights" to get personalized recommendations
              </p>
              <Button
                onClick={generateRecommendations}
                disabled={generatingRecs}
                className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {generatingRecs ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Insights
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Competitor Comparison Tab */}
      {activeTab === "comparison" && (
        <div className="space-y-4">
          <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Competitor Comparison
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Compare your brand presence against tracked competitors
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchCompetitorComparison}
                disabled={loadingComparison}
              >
                {loadingComparison ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>

            {loadingComparison ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
              </div>
            ) : competitorComparison ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Competitors</p>
                    <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {competitorComparison.total_competitors}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active on Social</p>
                    <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {competitorComparison.competitors?.filter(c => c.platforms_active > 0).length || 0}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg. Platforms</p>
                    <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {(competitorComparison.competitors?.reduce((sum, c) => sum + c.social_platforms.length, 0) / (competitorComparison.competitors?.length || 1)).toFixed(1)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Content Scraped</p>
                    <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {competitorComparison.competitors?.reduce((sum, c) => sum + c.content_scraped, 0) || 0}
                    </p>
                  </div>
                </div>

                {/* Competitor Table */}
                <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-[#30363d]' : 'border-gray-200'}`}>
                  <table className="w-full">
                    <thead className={darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Competitor</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Platforms</th>
                        <th className={`px-4 py-3 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Content</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-[#21262d]' : 'divide-gray-100'}`}>
                      {competitorComparison.competitors?.slice(0, 10).map((comp, idx) => (
                        <tr key={comp.id || idx} className={darkMode ? 'hover:bg-[#21262d]/50' : 'hover:bg-gray-50'}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {comp.logo_url ? (
                                <img src={comp.logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {comp.name?.charAt(0) || '?'}
                                  </span>
                                </div>
                              )}
                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {comp.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {comp.social_platforms?.map(platform => (
                                <Badge key={platform} className={`text-xs border-0 ${
                                  platform === 'linkedin' ? 'bg-blue-100 text-blue-700' :
                                  platform === 'instagram' ? 'bg-pink-100 text-pink-700' :
                                  platform === 'twitter' ? 'bg-sky-100 text-sky-700' :
                                  platform === 'youtube' ? 'bg-red-100 text-red-700' :
                                  platform === 'facebook' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {platform}
                                </Badge>
                              ))}
                              {comp.social_platforms?.length === 0 && (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {comp.content_scraped}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {comp.last_activity ? new Date(comp.last_activity).toLocaleDateString() : 'No data'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Platform Distribution Chart */}
                {competitorComparison.competitors?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`rounded-xl p-4 ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Platform Coverage
                      </h4>
                      <div className="space-y-2">
                        {['linkedin', 'instagram', 'twitter', 'youtube', 'facebook', 'tiktok'].map(platform => {
                          const count = competitorComparison.competitors?.filter(c => c.social_platforms?.includes(platform)).length || 0;
                          const percentage = (count / competitorComparison.total_competitors * 100) || 0;
                          return (
                            <div key={platform} className="flex items-center gap-2">
                              <span className={`text-xs w-20 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {platform.charAt(0).toUpperCase() + platform.slice(1)}
                              </span>
                              <div className={`flex-1 h-2 rounded-full ${darkMode ? 'bg-[#161b22]' : 'bg-gray-200'}`}>
                                <div 
                                  className={`h-2 rounded-full ${
                                    platform === 'linkedin' ? 'bg-blue-500' :
                                    platform === 'instagram' ? 'bg-pink-500' :
                                    platform === 'twitter' ? 'bg-sky-500' :
                                    platform === 'youtube' ? 'bg-red-500' :
                                    platform === 'facebook' ? 'bg-blue-600' :
                                    'bg-gray-500'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className={`text-xs w-8 text-right ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className={`rounded-xl p-4 ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Top Competitors by Content
                      </h4>
                      <div className="space-y-2">
                        {competitorComparison.competitors?.slice(0, 5).map((comp, idx) => (
                          <div key={comp.id || idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                                idx === 0 ? 'bg-[#DC143C] text-white' : darkMode ? 'bg-[#161b22] text-gray-400' : 'bg-gray-200 text-gray-500'
                              }`}>{idx + 1}</span>
                              <span className={`text-sm truncate max-w-[150px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {comp.name}
                              </span>
                            </div>
                            <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0 text-xs">
                              {comp.content_scraped} posts
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No comparison data available</p>
                <p className="text-sm mt-1">Click refresh to load competitor data</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Record Performance Tab */}
      {activeTab === "record" && (
        <div className="space-y-4">
          <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <h3 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Track Content Performance
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Record engagement metrics for your published content to help the AI learn what works best.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {content.slice(0, 12).map((item, index) => (
              <div 
                key={item.id || index}
                className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
              >
                <div className="p-4">
                  <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0 text-xs mb-2">
                    {item.topic_category}
                  </Badge>
                  <h4 className={`font-medium text-sm line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.topic}
                  </h4>
                  <p className={`text-xs mt-2 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.primary_post?.substring(0, 100)}...
                  </p>
                </div>
                <div className={`px-4 py-3 border-t ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRecordDialog(item)}
                    className="w-full"
                    data-testid={`record-btn-${index}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Record Performance
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Recording Dialog */}
      <PerformanceRecordDialog
        open={recordDialogOpen}
        onOpenChange={setRecordDialogOpen}
        darkMode={darkMode}
        selectedContent={selectedContent}
        performanceForm={performanceForm}
        setPerformanceForm={setPerformanceForm}
        recordPerformance={recordPerformance}
        recording={recording}
      />
    </div>
  );
}
